import React, { useState } from 'react';
import {
  Box,
  Container,
  Heading,
  Text,
  VStack,
  HStack,
  SimpleGrid,
  Badge,
  useColorModeValue,
  Button
} from '@chakra-ui/react';
import { motion } from 'framer-motion';

const MotionBox = motion(Box);

/**
 * Course Curriculum Breakdown
 * 5-week journey to building your first full-stack application
 */
const CourseCurriculumBreakdown = () => {
  const [hoveredPhase, setHoveredPhase] = useState(null);

  const phases = [
    {
      id: 1,
      name: 'Foundation',
      tech: 'Python',
      sessions: 11,
      color: '#3776ab',
      bgGradient: 'linear(to-br, #3776ab, #1f77b4)',
      description: 'Master Python fundamentals, OOP, data structures, and problem-solving skills',
      outcome: 'Strong programming foundation',
      week: 'Weeks 1-2'
    },
    {
      id: 2,
      name: 'Backend',
      tech: 'FastAPI',
      sessions: 2,
      color: '#009688',
      bgGradient: 'linear(to-br, #009688, #00897b)',
      description: 'Learn to build fast, modern APIs. Connect frontend to databases.',
      outcome: 'Production-ready APIs',
      week: 'Week 3'
    },
    {
      id: 3,
      name: 'Frontend',
      tech: 'React',
      sessions: 4,
      color: '#61dafb',
      bgGradient: 'linear(to-br, #61dafb, #00bcd4)',
      description: 'Build interactive user interfaces. Master state management and components.',
      outcome: 'Beautiful, responsive UI',
      week: 'Week 4'
    },
    {
      id: 4,
      name: 'Database',
      tech: 'SQLite3',
      sessions: 1,
      color: '#003b57',
      bgGradient: 'linear(to-br, #003b57, #01579b)',
      description: 'Design database schemas. Store and query data efficiently.',
      outcome: 'Data persistence mastered',
      week: 'Week 4'
    }
  ];

  const bgColor = useColorModeValue('white', 'gray.900');
  const cardBg = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.700');
  const textColor = useColorModeValue('gray.900', 'white');
  const mutedColor = useColorModeValue('gray.600', 'gray.400');

  return (
    <Box py={{ base: 20, md: 28 }} bg={bgColor}>
      <Container maxW="container.lg">
        <MotionBox
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: '-100px' }}
          transition={{ duration: 0.6 }}
        >
          <VStack align="start" spacing={16} w="full">
            {/* Header */}
            <VStack align="start" spacing={3} w="full">
              <Badge colorScheme="blue" px={3} py={1} borderRadius="full" fontSize="sm" fontWeight="700">
                ⏱️ 5 WEEKS TO YOUR FIRST FULL-STACK APP
              </Badge>
              <Heading as="h2" size="2xl" fontWeight="900" color={textColor}>
                Your Journey: From Zero to Shipping
              </Heading>
              <Text fontSize="lg" color={mutedColor} maxW="3xl" lineHeight="1.8">
                20 focused sessions that take you through Python, FastAPI, React, and SQLite. Build a real, working application from scratch. Perfect for beginners ready to get started.
              </Text>
            </VStack>

            {/* Phase Cards */}
            <SimpleGrid columns={{ base: 1, md: 2 }} spacing={6} w="full">
              {phases.map((phase, idx) => (
                <MotionBox
                  key={phase.id}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: idx * 0.1 }}
                  onMouseEnter={() => setHoveredPhase(phase.id)}
                  onMouseLeave={() => setHoveredPhase(null)}
                >
                  <Box
                    p={6}
                    bg={cardBg}
                    borderRadius="2xl"
                    border="2px solid"
                    borderColor={hoveredPhase === phase.id ? phase.color : borderColor}
                    _hover={{
                      boxShadow: `0 12px 32px ${phase.color}20`,
                      transform: 'translateY(-6px)',
                      borderColor: phase.color
                    }}
                    transition="all 0.3s cubic-bezier(0.4, 0, 0.2, 1)"
                    position="relative"
                    overflow="hidden"
                  >
                    {/* Phase Number Badge */}
                    <Box
                      position="absolute"
                      top={-2}
                      right={-2}
                      w={16}
                      h={16}
                      borderRadius="full"
                      bg={`${phase.color}15`}
                      display="flex"
                      alignItems="center"
                      justifyContent="center"
                    >
                      <Text fontSize="3xl" fontWeight="900" color={phase.color}>
                        {phase.id}
                      </Text>
                    </Box>

                    <VStack align="start" spacing={4} w="full" position="relative" zIndex={1}>
                      {/* Week Badge */}
                      <Badge
                        bg={`${phase.color}15`}
                        color={phase.color}
                        fontSize="xs"
                        fontWeight="700"
                        px={3}
                        py={1}
                        borderRadius="full"
                      >
                        {phase.week}
                      </Badge>

                      {/* Phase Name and Tech */}
                      <VStack align="start" spacing={1}>
                        <Text fontSize="sm" fontWeight="700" color={mutedColor} textTransform="uppercase" letterSpacing="wide">
                          Phase {phase.id}
                        </Text>
                        <Heading as="h3" size="xl" fontWeight="900" color={textColor}>
                          {phase.tech}
                        </Heading>
                        <Text fontSize="sm" fontWeight="600" color={phase.color}>
                          {phase.name} • {phase.sessions} sessions
                        </Text>
                      </VStack>

                      {/* Description */}
                      <Text fontSize="sm" color={mutedColor} lineHeight="1.6">
                        {phase.description}
                      </Text>

                      {/* Outcome Badge */}
                      <Box
                        w="full"
                        p={3}
                        bg={`${phase.color}08`}
                        borderLeft="4px solid"
                        borderColor={phase.color}
                        borderRadius="md"
                      >
                        <Text fontSize="xs" fontWeight="700" color={phase.color} textTransform="uppercase" letterSpacing="wider">
                          ✓ {phase.outcome}
                        </Text>
                      </Box>

                      {/* Progress Bar */}
                      <Box w="full" mt={2}>
                        <Box h={2} bg="gray.200" borderRadius="full" overflow="hidden">
                          <Box
                            h="full"
                            bg={phase.color}
                            w={`${(phase.sessions / 11) * 100}%`}
                            transition="width 0.6s ease"
                          />
                        </Box>
                      </Box>
                    </VStack>
                  </Box>
                </MotionBox>
              ))}
            </SimpleGrid>

            {/* Project Phase */}
            <MotionBox
              w="full"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.4 }}
            >
              <Box
                p={8}
                borderRadius="2xl"
                bgGradient="linear(135deg, #FF6B35 0%, #004E89 100%)"
                border="2px solid"
                borderColor="whiteAlpha.300"
                color="white"
                textAlign="center"
              >
                <VStack spacing={4}>
                  <Heading as="h3" size="xl" fontWeight="900">
                    🚀 Week 5: Build Your First Full-Stack App
                  </Heading>
                  <Text fontSize="lg" fontWeight="500" opacity={0.95}>
                    Combine everything you've learned in 2 hands-on sessions to build, test, and ship your first complete application
                  </Text>
                  <HStack spacing={4} justify="center" fontSize="sm" fontWeight="700" pt={2}>
                    <Box>✓ Python Backend</Box>
                    <Box>✓ React Frontend</Box>
                    <Box>✓ SQLite Database</Box>
                    <Box>✓ FastAPI Server</Box>
                  </HStack>
                  <Text fontSize="sm" opacity={0.9} pt={2}>
                    <strong>Total: 20 sessions | 5 weeks | 1 complete application</strong>
                  </Text>
                </VStack>
              </Box>
            </MotionBox>

            {/* Stats Footer */}
            <SimpleGrid columns={{ base: 2, md: 4 }} spacing={4} w="full" pt={4}>
              {[
                { label: 'Total Sessions', value: '20' },
                { label: 'Duration', value: '5 Weeks' },
                { label: 'Tech Stack', value: '4 techs' },
                { label: 'Projects Built', value: '1 Full App' }
              ].map((stat, idx) => (
                <MotionBox
                  key={idx}
                  initial={{ opacity: 0, y: 10 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.4, delay: 0.5 + idx * 0.05 }}
                >
                  <Box textAlign="center">
                    <Text fontSize="2xl" fontWeight="900" color="brand.600" mb={1}>
                      {stat.value}
                    </Text>
                    <Text fontSize="sm" color={mutedColor} fontWeight="600">
                      {stat.label}
                    </Text>
                  </Box>
                </MotionBox>
              ))}
            </SimpleGrid>

            {/* CTA */}
            <Box
              p={6}
              borderRadius="lg"
              bg={`brand.50`}
              border="1px solid"
              borderColor="brand.200"
              w="full"
              textAlign="center"
            >
              <VStack spacing={3}>
                <Text fontSize="sm" fontWeight="600" color="brand.900">
                  Ready to start your journey?
                </Text>
                <Text fontSize="lg" fontWeight="700" color="brand.900">
                  No prior experience needed. Just show up and build something amazing.
                </Text>
              </VStack>
            </Box>
          </VStack>
        </MotionBox>
      </Container>
    </Box>
  );
};

export default CourseCurriculumBreakdown;
